import React, { useState, useEffect, useContext } from 'react';
import { Table, Button, Row, Col, Space, Divider, Popconfirm, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined, QuestionCircleOutlined, EditOutlined, FilterOutlined } from '@ant-design/icons';
import CustomContent from '../../../components/Content/CustomContent';
import { fetchData, deleteData } from '../../service/dataServices';
import moment from 'moment';
import { useParams, useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import FormFilterDrawer from './FormFilterDrawer';
import UserContext from '../../../pages/layout/UserContext';
import checkFormTypePermission from '../../../components/Authorization/FormTypePermission';
import ForbiddenResult from '../../../components/Authorization/ForbiddenResult';

const { Title } = Typography;

const CustomFormShow = () => {
  const { formId } = useParams();
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const currentUser = useContext(UserContext);

  const hasReadPerm = checkFormTypePermission(currentUser, formId, 'read');
  const hasWritePerm = checkFormTypePermission(currentUser, formId, 'write');

  const [schema, setSchema] = useState(null);
  const [data, setData] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [userRefs, setUserRefs] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [filters, setFilters] = useState({ page: 0, size: 10 });

  fetchData('/api/users/user-refs', (resp) => setUserRefs(resp || []));
  fetchData(`/api/form-schema/${formId}`, (resp) => {
    setSchema(resp);
  }, formId);


  fetchData(`/api/form-record/all/${formId}`, (resp) => {
    setData(resp?.content || []);
    setTotalElements(resp?.totalElements || 0);
  }, {
    ...filters,
    page: pagination.current - 1,
    size: pagination.pageSize,
  }, formId?.length > 0 && pagination !== null);

  // 3. Delete Mutations
  const deleteMutation = deleteData(`/api/form-record`, `/api/form-record/all/${formId}`);

  const onDeleteSelected = () => {
    deleteMutation.mutate({ idList: selectedRowKeys.join(',') }, {
      onSuccess: () => {
        setSelectedRowKeys([]);
      }
    });
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  // Extract all fields from schema (flatten sections/rows/cols)
  const getAllFields = () => {
    const fields = [];
    if (schema && schema.sections) {
      schema.sections.forEach(section => {
        section?.rows?.forEach(row => {
          row?.cols?.forEach(col => {
            if (col.field) {
              fields.push(col.field);
            }
          });
        });
      });
    }
    return fields;
  };

  function translated(val) {
    return val?.[i18n.language] || val?.['en']
  }

  // 4. Build Dynamic Columns
  const buildColumns = () => {
    const columns = [];

    // Add Report ID as the first column (clickable to view/edit record)
    columns.push({
      title: t('customForm.recordId'),
      dataIndex: 'recordId',
      key: 'recordId',
      render: (text, record) => (
        <Button
          type="link"
          style={{ padding: 0, height: 'auto', fontWeight: 'inherit' }}
          onClick={() => history.push(`/custom-form-add/${formId}/${record.id}`)}
        >
          {text}
        </Button>
      ),
    });

    // Extract fields where showOnTable is true
    if (schema && schema.sections) {
      schema.sections.forEach(section => {
        section?.rows?.forEach(row => {
          row?.cols?.forEach(col => {
            if (col.field && col.field.showOnTable) {
              const fieldType = col.field.type;
              columns.push({
                title: translated(col.field.name),
                dataIndex: ['content', col.field.id], // Use field.id as content key - IDs are stable, names can change
                key: col.field.id,
                render: (val, record) => {
                  // Handle different types
                  if (typeof val === 'boolean') return val ? t('customForm.yes') : t('customForm.no');
                  if (fieldType === 'user_dropdown' && val) {
                    const user = userRefs.find(u => u.id === val);
                    return user ? user.name : val;
                  }
                  if (fieldType === 'date' && val) {
                    return moment(val).format('YYYY-MM-DD')
                  }
                  if (Array.isArray(val)) {
                    return val.map(f => (f && typeof f === 'object') ? (f.name || f.url) : f).filter(Boolean).join(', ');
                  }
                  //Handle TranslatableInput objects
                  if (fieldType === 'text' || fieldType === 'long_text') {
                    if (val && typeof val === 'object') {
                      return val[i18n.language] || val.en || val.ar || '';
                    }
                    return val || '';
                  }
                  if (val && typeof val === 'object') {
                    return val.name || val.url || '';
                  }
                  return val;
                }
              });
            }
          });
        });
      });
    }

    // Add Actions Column
    columns.push(
      {
        title: t('customForm.createdBy'),
        dataIndex: 'userName',
        key: 'userName',
        sorter: true,
      },
      {
        title: t('customForm.createdDate'),
        dataIndex: 'createdTime',
        key: 'createdTime',
        render: (text) => text ? moment(text).format('YYYY-MM-DD HH:mm') : '-',
      },
      {
        title: t('customForm.action'),
        key: 'action',
        fixed: 'right',
        render: (_, record) => (
          <Space size="middle">
            {hasWritePerm && (
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => history.push(`/custom-form-add/${formId}/${record.id}`)}
              >
                {t('customForm.edit')}
              </Button>
            )}
          </Space>
        ),
      });

    return columns;
  };

  // TODO: dropdown translation

  if (!hasReadPerm) {
    return <ForbiddenResult />;
  }

  return (
    <CustomContent>
      <Row justify="space-between" align="middle">
        <Col>
          <Title level={4}>{translated(schema?.name) || t('customForm.formRecords')}</Title>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<FilterOutlined />}
              onClick={() => setDrawerVisible(true)}
            >
              {t('customForm.filter')}
            </Button>
            {hasWritePerm && (
              <Popconfirm
                title={t('customForm.deleteRecords')}
                description={t('customForm.deleteConfirm', { count: selectedRowKeys.length })}
                onConfirm={onDeleteSelected}
                disabled={selectedRowKeys.length === 0}
                icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  disabled={selectedRowKeys.length === 0}
                >
                  {t('customForm.deleteSelected')}
                </Button>
              </Popconfirm>
            )}
            {hasWritePerm && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => history.push(`/custom-form-add/${formId}`)}
              >
                {t('customForm.addNewEntry')}
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <Divider />

      <Table
        rowKey="id"
        columns={buildColumns()}
        dataSource={data}
        pagination={{
          ...pagination,
          total: totalElements,
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
        rowSelection={hasWritePerm ? {
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        } : undefined}
        scroll={{ x: 'max-content' }}
      />

      <FormFilterDrawer
        drawerVisible={drawerVisible}
        setDrawerVisible={setDrawerVisible}
        setFilters={setFilters}
        filters={filters}
        formSchema={{ fields: getAllFields() }}
      />
    </CustomContent>
  );
};

export default CustomFormShow;